import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { EMPTY_FILTERS } from '@rs/contracts'
import { qk, useList, useOne } from '../../shared/queries.ts'
import { useAuth } from '../../app/auth.tsx'
import { fetchCustomers, fetchVendors } from '../catalog/api.ts'
import { fetchOrderBoardSummary } from '../az-sales/orders-api.ts'

/**
 * ══ HAI DÒNG BÁO CỦA MỌI MÀN RT — chép `AzPage` (`Az.tsx:596-606`) ════════════════════════════
 *
 * ⭐ BẢN CŨ DỰNG CHÚNG MỘT LẦN Ở THÂN TRANG, nên **mọi mục con của RT đều có**: chưa có danh mục
 * thì màn nào cũng nói, sale xem phạm vi hẹp thì màn nào cũng nhắc. rs-erp dựng màn theo từng tệp
 * nên hai dòng này chỉ có ở `az-sales/OrdersTab.tsx` — anh chốt 19/09/2026 làm cho TẤT CẢ màn RT.
 *
 * ⚠ CỐ Ý KHÔNG ÔM TIÊU ĐỀ TRANG. Bản cũ gộp cả `PageHeader` vào cùng chỗ, nhưng tám màn RT của
 *   rs-erp đang dùng bốn kiểu tiêu đề khác nhau (`azStats.rt.title`, `az.opscat.*`, `nav.*`…).
 *   Gộp nốt là đổi tiêu đề của những màn anh chưa soi — việc đó tách riêng, hỏi trước.
 *
 * ⚠ CHỈ HỎI SỐ ĐƠN KHI THẬT SỰ CẦN. Dòng xanh cần số đơn (`az.scopeOwn` có `{{n}}`). Vai nhìn được hết đơn
 *   (admin · kế toán · điều hành) không bao giờ thấy dòng đó, nên `enabled` tắt hẳn lượt hỏi cho họ.
 * ⭐ SỐ ĐƠN ĐẾM Ở MÁY CHỦ (đợt Đ4): trước đây `fetchOrders` kéo TOÀN BỘ đơn qua nhiều trang chỉ để lấy `.length`.
 *   Nay đọc `scopeCount` của `POST /az/orders/bang/tom-tat` (đã siết phạm vi) = MỌI đơn của sale, GỒM nháp và đơn ĐÃ HUỶ
 *   như bản cũ `myJobs.length` (`Az.tsx:604`, R0220 — trước 23/10 cộng hai chip "Tất cả" + "Nháp" nên thiếu đúng số đơn huỷ);
 *   KHÔNG gồm đơn hệ cũ (chip của màn cũng bỏ — anh chốt 21/10). Khoá dưới `['orders', …]` ⇒ ghi đơn xong (`qk.orders()`) là
 *   số này tự làm mới.
 *
 * ⚠ Màn nào gắn component này PHẢI nạp nhóm chuỗi `az` và `nav` (cổng `i18n-key-group`).
 */
export const RtNotices = (): JSX.Element | null => {
  const { t } = useTranslation()
  const { me } = useAuth()
  const { rows: customers } = useList(qk.customers(), fetchCustomers)
  const { rows: vendors } = useList(qk.vendors(), fetchVendors)

  const scopeOwn = me !== null && (me.azSalesScope ?? (me.role === 'sale' ? 'own' : 'all')) === 'own'
  /* ⚠ Đếm NGAY TRONG lời gọi, không lúc vẽ: thân phản hồi lệch khuôn (máy chủ cũ, lỗi mạng) thành LỖI TRUY VẤN, không
     thành màn trắng — dòng báo này nằm trên MỌI màn RT. Khoá riêng (không dùng chung khoá tóm tắt của màn Đơn hàng:
     cùng khoá mà khác kiểu dữ liệu là đọc nhầm của nhau). */
  const { data: nOrders } = useOne(qk.orderScopeCount(),
    () => fetchOrderBoardSummary(EMPTY_FILTERS).then((s) => s.scopeCount ?? s.counts.all + s.counts.draft), { enabled: scopeOwn })

  /* Danh mục CHƯA TẢI XONG thì im lặng — `[]` lúc đang tải trông hệt như "kho rỗng", và hộp vàng
     nháy lên rồi biến mất ở mỗi lần vào màn là thứ người dùng học cách phớt lờ. */
  const needCatalog = customers !== null && vendors !== null
    && (customers.length === 0 || vendors.length === 0)

  if (!needCatalog && !scopeOwn) return null

  return (
    <>
      {/* Kho RT chưa có Khách hàng / NCC thì KHÔNG tạo nổi đơn nào — nói ngay, kèm lối sang Danh
          mục (bản cũ `Az.tsx:597`). */}
      {needCatalog && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
          {t('az.needCatalog')}{' '}
          <a href="/catalog" className="font-bold underline">{t('nav.catalog')} ›</a>
        </div>
      )}

      {/* Sale chỉ thấy đơn của khách mình phụ trách — nói THẲNG, không để họ tưởng mất đơn
          (bản cũ `Az.tsx:603`). */}
      {scopeOwn && (
        <div className="text-[11px] bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5 text-sky-700">
          👤 {t('az.scopeOwn', { n: nOrders ?? 0 })}
        </div>
      )}
    </>
  )
}
